import { buildGoalDetailPath } from "@utils/goalRoute";
import type { GoalActivationSource } from "./useGoalActivationFlow";

const STORAGE_KEY = "goal-activation-continuation";
const MAX_AGE_MS = 2 * 60 * 60 * 1000;

export type GoalActivationContinuation = {
  ownerId: string;
  goalId: string;
  goalTitle?: string;
  source: GoalActivationSource;
  idempotencyKey: string;
  phase: "checkout" | "activating";
  createdAt: number;
};

export function readGoalActivationContinuation(
  ownerId: string,
): GoalActivationContinuation | undefined {
  if (!ownerId || typeof window === "undefined") {
    return;
  }
  try {
    const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null");
    if (!value) {
      return;
    }
    if (
      value.ownerId !== ownerId ||
      typeof value.goalId !== "string" ||
      !value.goalId ||
      typeof value.idempotencyKey !== "string" ||
      !value.idempotencyKey ||
      !["chat", "signal", "plan", "my_goals"].includes(value.source) ||
      !["checkout", "activating"].includes(value.phase) ||
      !Number.isFinite(value.createdAt) ||
      value.createdAt > Date.now() ||
      Date.now() - value.createdAt > MAX_AGE_MS
    ) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    return value;
  } catch {
    return;
  }
}

export function saveGoalActivationContinuation(
  value: GoalActivationContinuation,
): void {
  if (!value.ownerId || typeof window === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* Storage can be unavailable in private WebViews. */
  }
}

export function clearGoalActivationContinuation(
  ownerId: string,
  attemptKey?: string,
): void {
  const current = readGoalActivationContinuation(ownerId);
  if (!current || (attemptKey && current.idempotencyKey !== attemptKey)) {
    return;
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* No persisted continuation to remove. */
  }
}

/** Checkout confirms the purchase; the destination still verifies server entitlement before activation. */
export function resumeGoalActivationCheckout(
  ownerId: string,
  replace: (href: string) => void,
): boolean {
  const pending = readGoalActivationContinuation(ownerId);
  if (!pending) {
    return false;
  }
  saveGoalActivationContinuation({ ...pending, phase: "activating" });
  replace(buildGoalDetailPath(pending.goalId));
  return true;
}
